// src/pages/AboutPage.js

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Page,
  Container,
  Breadcrumb,
  Header,
  HeaderLogo,
  Eyebrow,
  Title,
  Lead,
  Section,
  SectionTitle,
  Prose,
  CardGrid,
  Card
} from '../components/ContentPage';
import { siteMeta } from '../seo/siteMeta';

const principles = [
  {
    title: 'AI where it helps',
    text: 'We add AI to a product when it removes real work for the user, such as categorizing expenses or answering questions about your own data.'
  },
  {
    title: 'Built for production',
    text: 'We design and operate the cloud services, data pipelines, and AI integrations behind every product we ship.'
  },
  {
    title: 'User centered',
    text: 'We refine our apps continuously based on user feedback and behavior.'
  },
  {
    title: 'Simple by default',
    text: 'We aim for interfaces that make complex tasks simple and accessible for everyone.'
  }
];

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO routeKey="about" />
      <Page>
        <Container>
          <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'About' }]} />

          <Header>
            <HeaderLogo src="/logo-xadevs-192.webp" alt="XADevs logo" width="96" height="96" />
            <div>
              <Eyebrow>About XADevs</Eyebrow>
              <Title>We build AI products and software systems</Title>
              <Lead>
                XADevs LLC is a US-registered software company that designs, builds, and operates
                AI products and the software systems behind them: AI-native features, cloud
                backends, and cross-platform apps.
              </Lead>
            </div>
          </Header>

          <Section aria-labelledby="products-heading">
            <SectionTitle id="products-heading">Our products</SectionTitle>
            <CardGrid>
              <Card>
                <h3>
                  <Link to="/volpetrack">VolpeTrack</Link>
                </h3>
                <p>
                  An AI-powered budget tracker and personal finance assistant for iOS and Android.
                  It categorizes expenses automatically, shows spending reports, tracks goals, and
                  includes a built-in AI assistant in 10 languages.
                </p>
              </Card>
              <Card>
                <h3>
                  <Link to="/holdthen">HoldThen</Link>
                </h3>
                <p>
                  A B2B international logistics platform that unifies package forwarding,
                  consolidation, inventory, billing, and shipment tracking. Its waitlist is open at
                  holdthen.com.
                </p>
              </Card>
            </CardGrid>
          </Section>

          <Section aria-labelledby="how-heading">
            <SectionTitle id="how-heading">How we work</SectionTitle>
            <CardGrid>
              {principles.map((principle) => (
                <Card key={principle.title}>
                  <h3>{principle.title}</h3>
                  <p>{principle.text}</p>
                </Card>
              ))}
            </CardGrid>
          </Section>

          <Section aria-labelledby="contact-heading">
            <SectionTitle id="contact-heading">Contact</SectionTitle>
            <Prose>
              <p>
                For general inquiries, write to{' '}
                <a href={`mailto:${siteMeta.emails.general}`}>{siteMeta.emails.general}</a>. For
                help with a product, visit the <Link to="/support">Support Center</Link> or write
                to <a href={`mailto:${siteMeta.emails.support}`}>{siteMeta.emails.support}</a>.
                Privacy questions go to{' '}
                <a href={`mailto:${siteMeta.emails.privacy}`}>{siteMeta.emails.privacy}</a>.
              </p>
              <p>
                You can also find XADevs on <a href="https://x.com/XADevs">X</a>,{' '}
                <a href="https://github.com/xadevs">GitHub</a>, the{' '}
                <a href="https://apps.apple.com/us/developer/xadevs-llc/id1827763153">App Store</a>,
                and <a href="https://play.google.com/store/apps/dev?id=7574937838146582908">Google Play</a>.
              </p>
            </Prose>
          </Section>
        </Container>
      </Page>
    </>
  );
};

export default AboutPage;
